import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Plus, Minus } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal } from "@/components/site/Reveal";
import { services } from "@/data/content";

export const Services = () => {
  const [active, setActive] = useState(0);
  const [openMobile, setOpenMobile] = useState(0);
  const current = services[active];

  return (
    <section id="sluzby" className="scroll-mt-24 bg-card" data-testid="services-section">
      <div className="mx-auto max-w-7xl px-5 py-20 lg:px-10 lg:py-28">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-accent">Služby</p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
            S čím Vám pomůžeme
          </h2>
        </Reveal>

        {/* Desktop: interactive list + photo */}
        <div className="mt-12 hidden gap-12 lg:grid lg:grid-cols-12">
          <div className="lg:col-span-5">
            <ul className="flex flex-col">
              {services.map((s, i) => (
                <li key={s.id}>
                  <button
                    data-testid={`service-tab-${s.id}`}
                    onClick={() => setActive(i)}
                    className={`group flex w-full items-baseline gap-5 border-b border-border py-6 text-left transition-colors duration-200 ${
                      i === active ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <span className={`text-sm font-semibold ${i === active ? "text-accent" : "text-sand"}`}>
                      {s.number}
                    </span>
                    <span className="flex-1">
                      <span className={`block text-xl font-semibold tracking-tight ${i === active ? "" : ""}`}>
                        {s.title}
                      </span>
                      {i === active && (
                        <span className="mt-2 block text-sm leading-relaxed text-muted-foreground">
                          {s.short}
                        </span>
                      )}
                    </span>
                    <ArrowRight
                      className={`h-4 w-4 shrink-0 transition-all duration-200 ${
                        i === active ? "translate-x-0 text-accent opacity-100" : "-translate-x-1 opacity-0"
                      }`}
                    />
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-7">
            <AnimatePresence mode="wait">
              <motion.div
                key={current.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                data-testid="service-detail"
              >
                <img
                  src={current.image}
                  alt={current.imageAlt}
                  className="h-[340px] w-full rounded-md object-cover"
                  loading="lazy"
                />
                <p className="mt-5 max-w-2xl text-sm leading-relaxed text-muted-foreground">
                  {current.description}
                </p>
                <ul className="mt-5 grid grid-cols-2 gap-x-6 gap-y-2">
                  {current.works.map((w) => (
                    <li key={w} className="flex items-start gap-2 text-sm text-foreground">
                      <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                      {w}
                    </li>
                  ))}
                </ul>
                <Link
                  to={current.link}
                  data-testid="service-more-link"
                  className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-accent transition-colors hover:text-accent/80"
                >
                  Více o službě <ArrowRight className="h-4 w-4" />
                </Link>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Mobile: expandable blocks */}
        <div className="mt-8 flex flex-col lg:hidden" data-testid="services-mobile">
          {services.map((s, i) => {
            const open = openMobile === i;
            return (
              <div key={s.id} className="border-b border-border">
                <button
                  data-testid={`service-mobile-${s.id}`}
                  onClick={() => setOpenMobile(open ? -1 : i)}
                  className="flex min-h-[56px] w-full items-center gap-4 py-4 text-left"
                >
                  <span className="text-sm font-semibold text-accent">{s.number}</span>
                  <span className="flex-1 text-lg font-semibold text-foreground">{s.title}</span>
                  {open ? (
                    <Minus className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Plus className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>
                <AnimatePresence initial={false}>
                  {open && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.28, ease: "easeOut" }}
                      className="overflow-hidden"
                    >
                      <div className="pb-6">
                        <img src={s.image} alt={s.imageAlt} className="h-48 w-full rounded-md object-cover" loading="lazy" />
                        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{s.short}</p>
                        <ul className="mt-3 flex flex-col gap-1.5">
                          {s.works.map((w) => (
                            <li key={w} className="flex items-start gap-2 text-sm">
                              <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                              {w}
                            </li>
                          ))}
                        </ul>
                        <Link to={s.link} className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-accent">
                          Více o službě <ArrowRight className="h-4 w-4" />
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
